import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchListingCoverageReport,
  type ListingCoverageChannel,
  type ListingCoverageReportResponse,
  type ListingCoverageState,
} from '../api/reports';
import { ReportGeneratedHeader } from '../components/reports/ReportGeneratedHeader';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

function channelLabel(channel: string): string {
  if (channel === 'shopify') return 'Shopify';
  if (channel === 'manapool') return 'Manapool';
  return channel;
}

function coverageStateLabel(state: ListingCoverageState): string {
  switch (state) {
    case 'measured':
      return 'Current';
    case 'stale':
      return 'Stale';
    case 'never_run':
      return 'Never run';
    case 'missing_from_snapshot':
      return 'Not in snapshot';
    case 'error':
      return 'Unavailable';
    default:
      return state;
  }
}

function formatCount(value: number | null): string {
  return value == null ? '—' : String(value);
}

function formatCoveragePct(value: number | null): string {
  return value == null ? '—' : `${value.toFixed(2)}%`;
}

function formatAgeMinutes(minutes: number | null): string | null {
  if (minutes == null) return null;
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 48) return rest ? `${hours}h ${rest}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const hourRest = hours % 24;
  return hourRest ? `${days}d ${hourRest}h` : `${days}d`;
}

export const ReportsListingCoveragePage: React.FC = () => {
  const [report, setReport] = useState<ListingCoverageReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchListingCoverageReport();
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listing coverage');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports: Listing Coverage</h1>
          <p style={styles.subtitle}>
            Expected versus listed item counts per enabled channel, taken from the latest
            successful parity job. This page does not scrape channels and does not replace
            Channel Parity remediation.
          </p>
        </div>
        <div style={styles.headerLinks}>
          <Link to="/reports/channel-parity" style={styles.linkButton}>Open Channel Parity</Link>
        </div>
      </div>

      <ReportGeneratedHeader
        generatedAt={report?.generated_at}
        onRefresh={() => {
          void loadReport();
        }}
        refreshing={isLoading}
      />

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <FreshnessBanner report={report} />
          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Enabled channels</h2>
              <span style={styles.metaText}>
                Stale after {report.stale_after_minutes} minutes
              </span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Channel</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Expected</th>
                    <th style={styles.th}>Listed</th>
                    <th style={styles.th}>Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {report.channels.length === 0 && (
                    <tr>
                      <td colSpan={5} style={styles.emptyCell}>
                        No enabled Shopify or Manapool listing channels.
                      </td>
                    </tr>
                  )}
                  {report.channels.map((channel) => (
                    <CoverageRow key={channel.channel} channel={channel} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

function FreshnessBanner({ report }: { report: ListingCoverageReportResponse }) {
  const age = formatAgeMinutes(report.age_minutes);
  const finished = report.job_finished_at ? formatInstant(report.job_finished_at) : null;

  let title = 'Current';
  let detail = 'Counts match the latest successful parity job.';
  let tone = colors.success;
  if (report.freshness === 'never_run') {
    title = 'Never run';
    detail = 'No successful parity job has been recorded. Coverage is unavailable, not 0%.';
    tone = colors.warning;
  } else if (report.freshness === 'stale') {
    title = 'Stale';
    detail = age
      ? `Latest successful parity job is ${age} old. Counts below are from that snapshot, not a live scrape.`
      : 'Latest successful parity job is older than the stale window. Counts below are from that snapshot, not a live scrape.';
    tone = colors.warning;
  }

  return (
    <div style={{ ...styles.banner, borderColor: tone }} role="status">
      <strong style={{ color: tone }}>{title}</strong>
      <span style={styles.bannerDetail}>{detail}</span>
      {finished && report.freshness !== 'never_run' && (
        <span style={styles.metaText}>Snapshot finished {finished}</span>
      )}
    </div>
  );
}

function CoverageRow({ channel }: { channel: ListingCoverageChannel }) {
  return (
    <tr>
      <td style={styles.tdStrong}>{channelLabel(channel.channel)}</td>
      <td style={styles.td}>{coverageStateLabel(channel.coverage_state)}</td>
      <td style={styles.td}>{formatCount(channel.expected_items)}</td>
      <td style={styles.td}>{formatCount(channel.listed_items)}</td>
      <td style={styles.td}>{formatCoveragePct(channel.coverage_pct)}</td>
    </tr>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    maxWidth: '720px',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  headerLinks: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
  banner: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  },
  bannerDetail: {
    color: colors.text,
    fontSize: typography.body.fontSize,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    flexWrap: 'wrap',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  metaText: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '640px',
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
};
